import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import { ErrorService } from '../../../../core/services/error.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginRequestDto } from '../../dtos/login-request.dto';
import { LoginResponseDto } from '../../dtos/login-response.dto';
import { StorageService } from '../../../../core/services/storage.service';
import { AuthenticationFormFactory } from '../../mappers/authentication-form.factory';
import { MemoryService } from '../../../../core/services/memory.service';
import { ResetPasswordDialog } from '../../dialogs/reset-password/reset-password.dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCodes } from '../../../../core/enums/error-codes.enum';
import { MigrateAdminDialog } from '../../dialogs/migrate-admin/migrate-admin.dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupCustomerIdDialog } from '../../dialogs/lookup-customer-id/lookup-customer-id.dialog';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslocoPipe],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  customerIdSelector!: FormControl;
  loginForm!: FormGroup;

  hidePassword: boolean = true;
  error?: string | null = null;

  showMigration: boolean = false;

  constructor(private authenticationService: AuthenticationService, private translocoService: TranslocoService, private errorService: ErrorService, private router: Router, private dialog: MatDialog, private storageService: StorageService, private fb: FormBuilder, private memoryService: MemoryService) {
  }

  ngOnInit(): void {
    if (!this.memoryService.customerId) {
      this.showMigration = true;
      this.customerIdSelector = new FormControl<number | null>(null, Validators.required);
    }

    this.loginForm = AuthenticationFormFactory.createLoginForm(this.fb);
  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  login() {
    if (this.loginForm.invalid || this.customerIdSelector?.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    if (this.customerIdSelector) {
      this.memoryService.customerId = this.customerIdSelector.value;
    }

    let loginRequestDto: LoginRequestDto = this.loginForm.value;
    loginRequestDto.customerId = this.memoryService.customerId!;

    this.authenticationService.login(loginRequestDto).subscribe({
      next: (loginResponseDto: LoginResponseDto) => {
        this.error = null;
        this.memoryService.accessToken = loginResponseDto.accessToken;
        this.storageService.storeRefreshToken(loginResponseDto.refreshToken);
        this.storageService.storeEmail(loginRequestDto.email);
        this.router.navigate(['/customer']);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status == 401) {
          if (error.error?.errorCode && error.error.errorCode == ErrorCodes.emailIsCustomerKey) {
            this.dialog.open(MigrateAdminDialog, {
              data: { loginRequestDto: loginRequestDto },
              autoFocus: false
            }).afterClosed().subscribe((customerId: number) => {
              if (customerId) {
                this.customerIdSelector.setValue(customerId);
              }
            });
            return;
          }

          this.error = this.translocoService.translate('error.invalid_login');
          return;
        }

        this.error = this.errorService.handleError(error);
      }
    });
  }

  resetPassword() {
    let loginRequestDto: LoginRequestDto = this.loginForm.value;

    this.dialog.open(ResetPasswordDialog, {
      data: { email: loginRequestDto.email, customerId: (this.memoryService.customerId) ? this.memoryService.customerId : this.customerIdSelector.value },
      autoFocus: false
    });
  }

  lookupCustomerId() {
    this.dialog.open(LookupCustomerIdDialog, {autoFocus: false}).afterClosed().subscribe((customerId: number) => {
      if (customerId) {
        this.customerIdSelector.setValue(customerId);
      }
    });
  }

  migrateAdmin() {
    const loginRequestDto: LoginRequestDto = {email: "", password: "", customerId: -1};

    this.dialog.open(MigrateAdminDialog, {
      data: { loginRequestDto: loginRequestDto },
      autoFocus: false
    }).afterClosed().subscribe((customerId: number) => {
      if (customerId) {
        this.customerIdSelector.setValue(customerId);
      }
    });
  }
}
